import { useState } from "react";
import Uploader from "./components/Uploader";
import SettingsPanel from "./components/SettingsPanel";
import TemplateCard from "./components/TemplateCard";
import InstallButton from "./components/InstallButton";
import { useImages } from "./hooks/useImages";

export default function App() {
  const {
    images,
    defaults,
    hydrated,
    setDefaults,
    addFiles,
    removeImage,
    removeAll,
    updateSettings,
    toggleCell,
    resetProgress,
  } = useImages();
  const [showHowTo, setShowHowTo] = useState(false);

  const handleRemoveAll = () => {
    if (window.confirm(`Remove all ${images.length} images? Their progress will also be cleared.`)) {
      removeAll();
    }
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <h1>Crochet & Cross-Stitch Templates</h1>
          <p>Drop a picture, get a chart you can mark off as you go.</p>
        </div>
        <div className="row">
          <InstallButton />
          <button className="button ghost" onClick={() => setShowHowTo((v) => !v)}>
            {showHowTo ? "Hide help" : "How to use"}
          </button>
        </div>
      </header>

      {showHowTo && (
        <div className="card" style={{ marginBottom: 18 }}>
          <ol style={{ marginTop: 0 }}>
            <li>Pick a project type (Crochet for big chunky cells, Cross-stitch for finer charts).</li>
            <li>Drop one or more pictures onto the box below (or tap to choose).</li>
            <li>
              Each picture turns into a numbered grid of solid colors. Adjust grid
              size, number of colors, and merging until it looks right.
            </li>
            <li>
              Tap <strong>Download PNG</strong> to save the chart for printing.
            </li>
            <li>
              When stitching, tap <strong>Mark while crocheting</strong> and tap each
              square as you finish it. Your pictures and progress are saved on this
              device — you can close the tab and come back.
            </li>
          </ol>
        </div>
      )}

      <section className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>New picture defaults</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          These settings are applied to every new picture you add. You can still
          tweak each one individually.
        </p>
        <SettingsPanel
          settings={defaults}
          onChange={(patch) =>
            setDefaults({ ...defaults, ...(patch as Partial<typeof defaults>) })
          }
          compact
        />
      </section>

      <section style={{ marginBottom: 18 }}>
        <Uploader
          onFiles={addFiles}
          hint="JPG / PNG · You can add many at once"
        />
      </section>

      {images.length > 0 && (
        <section style={{ marginBottom: 12 }} className="row" >
          <span className="muted">
            {images.length} {images.length === 1 ? "picture" : "pictures"} · saved on this device
          </span>
          <button className="button ghost" onClick={handleRemoveAll}>
            Remove all images
          </button>
        </section>
      )}

      <section className="stack">
        {!hydrated && (
          <div className="card" style={{ textAlign: "center" }}>
            <p className="muted" style={{ margin: 0 }}>Loading saved pictures…</p>
          </div>
        )}
        {hydrated && images.length === 0 && (
          <div className="card" style={{ textAlign: "center" }}>
            <p className="muted" style={{ margin: 0 }}>
              No pictures yet. Drop something above to start.
            </p>
          </div>
        )}
        {images.map((img) => (
          <TemplateCard
            key={img.id}
            image={img}
            onSettingsChange={(patch) => updateSettings(img.id, patch)}
            onToggleCell={(idx) => toggleCell(img.id, idx)}
            onResetProgress={() => resetProgress(img.id)}
            onRemove={() => removeImage(img.id)}
          />
        ))}
      </section>
    </div>
  );
}
